"use client";

import Image from "next/image";
import Link from "next/link";
import { Plus, Truck } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useCart } from "@/hooks/useCart";
import { pushToDataLayer } from "@/lib/analytics/gtm";
import { readLastShippingPostcode, writeLastShippingPostcode } from "@/lib/commerce/shippingCalculator";
import type { CartSuggestionsResult } from "@/lib/cart/cartSuggestions";
import type { GapCandidate } from "@/lib/cart/gapFiller";

// Mini carrinho (2026-10-25): barra de frete grátis (no rodapé, perto dos
// botões), produto que completa o frete e carrossel "Complete sua compra"
// (depois dos produtos). Os dados vêm do servidor
// (/api/cart/native/free-shipping), com as mesmas regras de frete do
// checkout; sem CEP nunca promete frete grátis. O MiniCart chama o hook uma
// vez e passa o resultado para as duas partes.

const FALLBACK_IMAGE = "/images/brand/persi-materiais-eletricos-e-hidraulicos-ferramentas.webp";
const brl = (minor: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(minor / 100);

function readStoredPostcode(): string {
  try {
    return readLastShippingPostcode(window.localStorage);
  } catch {
    return "";
  }
}

export interface MiniCartUpsellState {
  data: CartSuggestionsResult | null;
  postcode: string;
  savePostcode: (digits: string) => void;
  adding: number | null;
  busy: boolean;
  add: (candidate: GapCandidate, origin: "gap" | "complemento") => Promise<void>;
}

export function useMiniCartUpsell(open: boolean): MiniCartUpsellState {
  const { cart, addItem, pendingItemKey } = useCart();
  // CEP digitado aqui vence; senão, o último CEP usado no site.
  const [postcodeOverride, setPostcodeOverride] = useState<string | null>(null);
  const [data, setData] = useState<CartSuggestionsResult | null>(null);
  const [adding, setAdding] = useState<number | null>(null);
  const sentEvents = useRef(new Set<string>());
  const postcode = postcodeOverride ?? (open && cart?.items.length ? readStoredPostcode() : "");

  const signature = (cart?.items ?? []).map((item) => `${item.key}:${item.quantity}`).join("|");
  useEffect(() => {
    if (!open || !signature) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void fetch(`/api/cart/native/free-shipping${postcode ? `?cep=${postcode}` : ""}`, { credentials: "same-origin", cache: "no-store", signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then((body: CartSuggestionsResult | null) => setData(body))
        .catch(() => undefined);
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, signature, postcode]);

  // Medição (GTM): cada evento uma vez por situação.
  useEffect(() => {
    const status = data?.status;
    if (!open || !status) return;
    const once = (key: string, event: Record<string, unknown>) => {
      if (sentEvents.current.has(key)) return;
      sentEvents.current.add(key);
      pushToDataLayer(event);
    };
    if (status.state === "missing" && data.showBar) {
      once(`bar:${status.missingMinor}`, { event: "fs_bar_shown", falta: status.missingMinor / 100, valor_minimo: status.minimumMinor / 100 });
    }
    if (status.state === "eligible") once(`unlocked:${signature}`, { event: "fs_unlocked", subtotal: cart?.subtotal ?? null });
    if (data.gapFiller) once(`gap:${data.gapFiller.productId}`, { event: "fs_gap_suggestion_shown", produto_id: data.gapFiller.productId, valor: data.gapFiller.priceMinor / 100 });
  }, [data, open, signature, cart?.subtotal]);

  async function add(candidate: GapCandidate, origin: "gap" | "complemento") {
    setAdding(candidate.productId);
    const result = await addItem({ productId: candidate.productId, quantity: 1 });
    setAdding(null);
    if (!result.success) return;
    if (origin === "gap") pushToDataLayer({ event: "fs_gap_suggestion_add", produto_id: candidate.productId, valor: candidate.priceMinor / 100 });
    else pushToDataLayer({ event: "complement_add", produto_id: candidate.productId, origem: "mini_carrinho" });
  }

  function savePostcode(digits: string) {
    try {
      writeLastShippingPostcode(window.localStorage, digits);
    } catch {
      // segue com o CEP só na tela
    }
    setPostcodeOverride(digits);
  }

  return { data, postcode, savePostcode, adding, busy: adding !== null || Boolean(pendingItemKey), add };
}

/** Barra de frete grátis compacta, no rodapé (acima do subtotal). */
export function MiniCartFreeShippingBar({ state }: { state: MiniCartUpsellState }) {
  const { data, postcode, savePostcode } = state;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const status = data?.status ?? null;
  const postcodeLabel = postcode ? `${postcode.slice(0, 5)}-${postcode.slice(5)}` : "";

  function submit(event: FormEvent) {
    event.preventDefault();
    const digits = draft.replace(/\D/g, "");
    if (digits.length !== 8) return;
    savePostcode(digits);
    setEditing(false);
  }

  function startEditing() {
    setDraft(postcodeLabel);
    setEditing(true);
  }

  const changeButton = (
    <button type="button" onClick={startEditing} className="shrink-0 text-[11px] text-muted underline underline-offset-2">
      CEP {postcodeLabel} · trocar
    </button>
  );

  if (status?.state === "no_postcode" || editing || (!postcode && !status)) {
    return (
      <form onSubmit={submit} className="mb-3">
        <p className="mb-1.5 text-xs text-slate-700">
          <Truck className="mr-1 inline h-3.5 w-3.5 text-secondary" aria-hidden="true" />
          {status?.state === "no_postcode" && status.fromMinor !== null
            ? <>Frete grátis a partir de <strong>{brl(status.fromMinor)}</strong> na região. Informe o CEP:</>
            : "Informe o CEP para ver o frete grátis:"}
        </p>
        <div className="flex gap-2">
          <label htmlFor="mini-cart-cep" className="sr-only">CEP</label>
          <input
            id="mini-cart-cep"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={9}
            placeholder="00000-000"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              // CEP completo: calcula sozinho, sem precisar do OK.
              const digits = event.target.value.replace(/\D/g, "");
              if (digits.length === 8) {
                savePostcode(digits);
                setEditing(false);
              }
            }}
            className="h-10 min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm"
          />
          <button type="submit" className="h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-white active:bg-primary-hover">OK</button>
        </div>
      </form>
    );
  }

  if (status && data?.showBar && (status.state === "missing" || status.state === "eligible")) {
    const progress = status.state === "eligible" ? 100 : Math.min(100, Math.round(((status.minimumMinor - status.missingMinor) / status.minimumMinor) * 100));
    const done = status.state === "eligible";
    return (
      <div className="mb-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="min-w-0 text-xs text-slate-700" aria-live="polite">
            {done
              ? <>🎉 <strong className="text-emerald-700">Frete grátis</strong> para {status.place}</>
              : <>Faltam {status.state === "missing" && status.kind === "percent" ? "cerca de " : ""}<strong className="text-heading">{brl(status.missingMinor)}</strong> para frete grátis</>}
          </p>
          {changeButton}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <div className="h-1.5 flex-1 rounded-full bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label="Progresso até o frete grátis">
            <div className={`h-full rounded-full transition-[width] duration-500 ease-out ${done ? "bg-emerald-500" : "bg-secondary"}`} style={{ width: `${progress}%` }} />
          </div>
          <Truck className={`h-4 w-4 shrink-0 ${done ? "text-emerald-600" : "text-slate-400"}`} aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (status?.state === "unavailable") {
    return (
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <p className="min-w-0 text-[11px] text-muted">{status.message}</p>
        {changeButton}
      </div>
    );
  }
  return null;
}

/** Produto que completa o frete + carrossel de complementos (depois dos itens). */
export function MiniCartSuggestions({ state }: { state: MiniCartUpsellState }) {
  const { data, adding, busy, add } = state;
  if (!data) return null;
  const gap = data.status?.state === "missing" ? data.gapFiller : null;
  if (!gap && data.complements.length === 0) return null;

  return (
    <div className="-mx-5 border-t bg-slate-50 px-5 py-3">
      {gap ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-white p-2">
          <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-50">
            <Image src={gap.image?.src || FALLBACK_IMAGE} alt="" fill sizes="40px" className="object-contain p-0.5" />
          </span>
          <p className="min-w-0 flex-1 text-[11px] leading-4 text-slate-700">
            Adicione <Link href={gap.href} className="font-semibold text-foreground hover:underline">{gap.name}</Link> por <strong>{brl(gap.priceMinor)}</strong> e ganhe frete grátis
          </p>
          <button type="button" disabled={busy} onClick={() => void add(gap, "gap")} className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-emerald-600 px-2.5 text-xs font-semibold text-white active:bg-emerald-700 disabled:opacity-60">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />{adding === gap.productId ? "…" : "Adicionar"}
          </button>
        </div>
      ) : null}

      {data.complements.length > 0 ? (
        <section aria-label={data.complementsTitle} className={gap ? "mt-3" : ""}>
          <p className="text-xs font-semibold text-heading">{data.complementsTitle}</p>
          <ul className="-mx-5 mt-2 flex snap-x gap-2 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {data.complements.map((item) => (
              <li key={item.productId} className="flex w-[104px] shrink-0 snap-start flex-col rounded-xl border border-slate-200 bg-white p-1.5">
                <Link href={item.href} className="relative mx-auto block h-16 w-16 overflow-hidden rounded-lg bg-slate-50">
                  <Image src={item.image?.src || FALLBACK_IMAGE} alt={item.image?.alt || item.name} fill sizes="64px" className="object-contain p-0.5" />
                </Link>
                {item.unlocksFreeShipping ? <span className="mt-1 self-start rounded bg-emerald-600 px-1 py-px text-[9px] font-bold uppercase text-white">frete grátis</span> : null}
                <p className="mt-1 line-clamp-2 text-[11px] leading-[14px] text-foreground">{item.name}</p>
                {item.regularPriceMinor && item.regularPriceMinor > item.priceMinor ? <p className="text-[10px] text-muted line-through">{brl(item.regularPriceMinor)}</p> : null}
                <p className="mb-1.5 text-xs font-bold text-heading">{brl(item.priceMinor)}</p>
                <button type="button" disabled={busy} onClick={() => void add(item, "complemento")} aria-label={`Adicionar ${item.name}`} className="mt-auto flex h-8 items-center justify-center gap-1 rounded-lg border border-primary text-[11px] font-semibold text-primary active:bg-primary active:text-white disabled:opacity-60">
                  <Plus className="h-3 w-3" aria-hidden="true" />{adding === item.productId ? "…" : "Adicionar"}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
